import { useMemo, useState } from 'react';

/**
 * useSortable — column sorting hook for any list of objects.
 *
 * Returns: { sorted, sortKey, sortDir, setSort, getProps(key) }
 *
 * Usage on table headers:
 *   <th {...getProps('name')}>Name <SortIcon dir={sortKey === 'name' ? sortDir : null} /></th>
 */
export function useSortable(items, { defaultKey = null, defaultDir = 'asc' } = {}) {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState(defaultDir);

  const setSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return items;
    const cmp = (a, b) => {
      let va = a?.[sortKey];
      let vb = b?.[sortKey];
      // Special: size like '1-10' / '500+' — convert to first integer
      if (sortKey === 'size') {
        const toNum = (s) => parseInt(String(s ?? '').replace(/[^0-9]/g, ''), 10) || 0;
        va = toNum(va); vb = toNum(vb);
      } else if (typeof va === 'string' && /^\d{4}-\d{2}-\d{2}/.test(va)) {
        // ISO date string — compare lex (same as chronological)
      } else if (typeof va === 'number' || typeof vb === 'number') {
        va = Number(va) || 0; vb = Number(vb) || 0;
      } else {
        va = (va ?? '').toString().toLowerCase();
        vb = (vb ?? '').toString().toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    };
    return [...items].sort(cmp);
  }, [items, sortKey, sortDir]);

  const getProps = (key) => ({
    onClick: () => setSort(key),
    className: 'cursor-pointer select-none hover:text-[#1F7A6E]',
    'data-testid': `sort-${key}`,
  });

  return { sorted, sortKey, sortDir, setSort, getProps };
}

import React from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/** Tiny inline sort indicator. */
export function SortIcon({ dir }) {
  if (dir === 'asc') return <ChevronUp size={11} className="inline -mt-0.5 text-[#1F7A6E]" />;
  if (dir === 'desc') return <ChevronDown size={11} className="inline -mt-0.5 text-[#1F7A6E]" />;
  return <ChevronsUpDown size={10} className="inline -mt-0.5 text-slate-300" />;
}
